import React, { useEffect, useState, useRef } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { db } from "../firebase/firebase";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { useCart } from "../contexts/CartContext";
import { useSale } from "../contexts/SaleContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCartShopping, faPills, faSyringe,
  faFlask, faLeaf, faStethoscope, faPrescriptionBottle, faBorderAll,
  faPumpMedical, faBaby, faPumpSoap, faBandage, faHeartPulse
} from "@fortawesome/free-solid-svg-icons";

const PAGE_SIZE = 10;

const Categories = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  // eslint-disable-next-line
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const { addToCart } = useCart();
  const { sale } = useSale();
  const dropdownRef = useRef(null);

  const getDiscountedPrice = (retailPrice) => {
    if (sale.isActive && sale.discountPercent > 0 && retailPrice) {
      return Math.round(retailPrice * (1 - sale.discountPercent / 100));
    }
    return null;
  };


  useEffect(() => {
    document.title = "Categories | dawaacare";
  }, [])

  useEffect(() => {
    const productsRef = collection(db, "products");
    const q = query(productsRef, orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setProducts(data);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching products:", error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset visible count when category changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [selectedCategory]);

  // Derive unique categories from products
  const categories = ["All", ...Array.from(new Set(products.map((p) => p.type).filter(Boolean))).sort()];

  const filtered = selectedCategory === "All"
    ? products
    : products.filter((p) => p.type === selectedCategory);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (name === "all") return faBorderAll;
    if (name.includes("tablet") || name.includes("pill") || name.includes("capsule")) return faPills;
    if (name.includes("syrup") || name.includes("liquid") || name.includes("drop")) return faFlask;
    if (name.includes("injection") || name.includes("vaccine") || name.includes("syringe")) return faSyringe;
    if (name.includes("cream") || name.includes("ointment") || name.includes("gel") || name.includes("lotion")) return faPumpMedical;
    if (name.includes("supplement") || name.includes("vitamin")) return faLeaf;
    if (name.includes("equipment") || name.includes("device") || name.includes("machine")) return faStethoscope;
    if (name.includes("baby") || name.includes("child")) return faBaby;
    if (name.includes("soap") || name.includes("wash") || name.includes("shampoo")) return faPumpSoap;
    if (name.includes("band") || name.includes("dressing") || name.includes("tape") || name.includes("aid")) return faBandage;
    if (name.includes("health") || name.includes("care") || name.includes("heart")) return faHeartPulse;
    return faPrescriptionBottle; // Default fallback
  };

  return (
    <div>
      <Header />

      <div className="categories-page" data-aos="fade-up">
        {/* Page Header */}
        <div className="categories-header">
          <h1 className="categories-title">Shop by Category</h1>
          <p className="categories-subtitle">
            Browse all our products organized by category
          </p>
        </div>

        {/* Category Circles Grid */}
        <div className="category-circles-container">
          {categories.map((cat) => (
            <div
              key={cat}
              className={`category-circle-item ${selectedCategory === cat ? "active" : ""}`}
              onClick={() => setSelectedCategory(cat)}
            >
              <div className="category-circle">
                <FontAwesomeIcon icon={getCategoryIcon(cat)} className="category-circle-icon" />
              </div>
              <span className="category-circle-name">{cat === "All" ? "All Categories" : cat}</span>
            </div>
          ))}
        </div>

        {/* Filter Result Row */}
        <div className="categories-filter-row">
          <div className="cat-result-title">
            <h2>{selectedCategory === "All" ? "All Products" : selectedCategory}</h2>
          </div>

          {!loading && (
            <span className="cat-result-count">
              {filtered.length} product{filtered.length !== 1 ? "s" : ""}
              {selectedCategory !== "All" ? ` in "${selectedCategory}"` : ""}
            </span>
          )}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="products-container">
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div className="product-card skeleton-card" key={i}>
                <div className="skeleton-image-box skeleton-shimmer" />
                <div className="skeleton-info">
                  <div className="skeleton-line skeleton-shimmer" style={{ width: "50%", height: "12px" }} />
                  <div className="skeleton-line skeleton-shimmer" style={{ width: "80%", height: "16px", marginTop: "8px" }} />
                  <div className="skeleton-line skeleton-shimmer" style={{ width: "40%", height: "14px", marginTop: "10px" }} />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="cat-empty-state">
            <div className="cat-empty-icon">📦</div>
            <h3>No products in this category</h3>
            <p>Try selecting a different category</p>
            <button className="cat-reset-btn" onClick={() => setSelectedCategory("All")}>
              View All Products
            </button>
          </div>
        ) : (
          <>
            <div className="products-container">
              {visible.map((product) => (
                <Link
                  to={`/product/${product.id}`}
                  style={{ textDecoration: "none" }}
                  key={product.id}
                >
                  <div className="product-card">
                    <div className="card-image-box">
                      {sale.isActive && sale.discountPercent > 0 && (
                        <span className="discount-badge">{sale.discountPercent}% OFF</span>
                      )}
                      {product.isOutOfStock && (
                        <span className="oos-overlay-badge">Out of Stock</span>
                      )}
                      <img src={product.picture} alt={product.name} />
                      {!product.isOutOfStock && (
                        <div className="add-to-cart-btn-wrapper">
                          <button
                            className="add-cart-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              const discountedPrice = getDiscountedPrice(product.retailPrice);
                              addToCart({ ...product, price: discountedPrice ?? product.price });
                            }}
                            title="Add to cart"
                          >
                            <FontAwesomeIcon icon={faCartShopping} />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="card-info">
                      <h3 className="product-type">{product.type}</h3>
                      <h4 className="product-name">{product.name}</h4>
                      <div className="price-container">
                        {getDiscountedPrice(product.retailPrice) ? (
                          <>
                            <span className="new-price">Rs. {getDiscountedPrice(product.retailPrice)}</span>
                            <span className="old-price">Rs. {product.retailPrice}</span>
                          </>
                        ) : (
                          <span className="new-price">Rs. {product.price}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {hasMore && (
              <div className="load-more-wrapper">
                <button
                  className="load-more-btn"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Categories;
